import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getBackendUrl, safeJson } from '../../utils/api';

// Торговый движок для управления сделками
export const useTradingEngine = (user = null) => {
  const [balance, setBalance] = useState(user ? 0.00 : 1000.00);
  const { tt } = useLanguage(); // Демо = $1000, авторизованные = $0 (загружается с сервера)
  const [activeTrades, setActiveTrades] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const tradeTimersRef = useRef(new Map());

  // Настройки клиента из CRM (кнопка вкл/выкл прибыль)
  const [clientSettings, setClientSettings] = useState({
    profitEnabled: true, // true = в прибыли, false = в убытке (управляется из CRM)
    payoutPercentage: 85, // Процент выплат из CRM (по умолчанию 85%)
    tradingEnabled: true, // Разрешена ли торговля
    maxTradeAmount: 1000, // Максимальная сумма сделки
    forceResultMode: 'auto' // 'auto', 'win', 'loss' - режим закрытия сделок из CRM
  });

  // Загружаем реальный баланс и историю сделок для авторизованных пользователей
  useEffect(() => {
    console.log('🔄 TradingEngine useEffect triggered, user:', user);
    
    // Clear previous data when user changes
    if (!user) {
      setActiveTrades([]);
      setTradeHistory([]);
      setBalance(1000.00); // Demo balance
      console.log('🚫 TradingEngine: No user, cleared data and set demo balance');
      return;
    }
    
    console.log('👤 TradingEngine: User exists, loading data for user ID:', user.id);
    
    const loadUserData = async () => {
        try {
          const token = localStorage.getItem('token');
          console.log('🔑 TradingEngine: Token check:', token ? 'exists' : 'missing');
          
          if (!token) {
            console.warn('⚠️ No token found, skipping data loading');
            setActiveTrades([]);
            setTradeHistory([]);
            setBalance(0.00);
            return;
          }
          
          // Clear previous data before loading new data
          console.log('🧹 Clearing previous trading data before reload');
          setActiveTrades([]);
          setTradeHistory([]);
          
          console.log('🌐 TradingEngine: Backend URL:', getBackendUrl());
          
          // Загружаем настройки клиента из CRM
          try {
            const settingsResponse = await fetch(`${getBackendUrl()}/api/trading/client-settings`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (settingsResponse.ok) {
              const settingsData = await safeJson(settingsResponse);
              console.log('⚙️ Настройки клиента загружены:', settingsData);
              setClientSettings(prevSettings => ({
                ...prevSettings,
                profitEnabled: settingsData.profit_enabled ?? true,
                payoutPercentage: settingsData.payout_percentage || 85,
                tradingEnabled: settingsData.trading_enabled ?? true,
                maxTradeAmount: settingsData.max_trade_amount || 1000,
                forceResultMode: settingsData.force_result_mode || 'auto'
              }));
            } else {
              console.warn('⚠️ Не удалось загрузить настройки клиента, используем настройки по умолчанию');
            }
          } catch (settingsError) {
            console.error('❌ Ошибка загрузки настроек клиента:', settingsError);
          }
          
          // Загружаем баланс
          console.log('💰 TradingEngine: Fetching balance from API...');
          const balanceResponse = await fetch(`${getBackendUrl()}/api/trading/balance`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          console.log('📊 TradingEngine: Balance API response status:', balanceResponse.status);
          
          if (balanceResponse.ok) {
            const balanceData = await safeJson(balanceResponse);
            console.log('💰 TradingEngine: Balance data received:', balanceData);
            
            // Add NaN protection when loading initial balance
            const apiBalance = parseFloat(balanceData.balance);
            const newBalance = isNaN(apiBalance) ? 0.00 : apiBalance;
            
            setBalance(newBalance);
            console.log(`✅ TradingEngine: Balance set to: $${newBalance}`);
            
            // Additional debugging for problematic balance values
            if (newBalance === 0) {
              console.warn('⚠️ TradingEngine: Balance is zero - check if client has trading balance in database');
            }
            if (isNaN(apiBalance)) {
              console.error('❌ TradingEngine: API returned non-numeric balance:', balanceData.balance);
            }
          } else {
            const errorText = await balanceResponse.text();
            console.error('❌ TradingEngine: Balance API error:', balanceResponse.status, errorText);
            setBalance(0.00);
          }
          
          // Загружаем историю сделок (теперь возвращает данные из CRM)
          const tradesResponse = await fetch(`${getBackendUrl()}/api/trading/trades`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (tradesResponse.ok) {
            const tradesData = await safeJson(tradesResponse);
            console.log('📊 Raw trades data from CRM received:', tradesData);
            const completedTrades = tradesData.filter(trade => trade.status !== 'active');
            
            // Дедупликация завершенных сделок по ID
            const uniqueCompletedTrades = completedTrades.filter((trade, index, arr) => 
              arr.findIndex(t => t.id === trade.id) === index
            );
            
            setTradeHistory(uniqueCompletedTrades);
            console.log(`📈 Loaded ${uniqueCompletedTrades.length} unique completed trades (${completedTrades.length} total, ${completedTrades.length - uniqueCompletedTrades.length} duplicates removed)`);
            console.log('📊 Trade history data:', uniqueCompletedTrades);
          } else {
            console.error('❌ Failed to fetch trades:', tradesResponse.status, tradesResponse.statusText);
            if (tradesResponse.status === 401) {
              console.error('🔐 Unauthorized - token may be invalid');
            }
          }

          // Загружаем открытые сделки (теперь возвращает данные из CRM)
          console.log('🔄 Loading active trades for user...');
          const openTradesResponse = await fetch(`${getBackendUrl()}/api/trading/open-trades`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (openTradesResponse.ok) {
            const openTradesData = await safeJson(openTradesResponse);
            console.log('🔄 Received active trades data from CRM:', openTradesData);
            const now = new Date();
            const activeTrades = [];
            
            openTradesData.forEach(trade => {
              const expiresAt = new Date(trade.expires_at);
              
              // Если сделка еще не истекла, добавляем ее в активные
              if (expiresAt > now) {
                const activeTrade = {
                  id: trade.id,
                  asset: trade.asset,
                  direction: trade.direction,
                  amount: trade.amount,
                  strikePrice: trade.strike_price,
                  createdAt: new Date(trade.created_at),
                  expiresAt: expiresAt,
                  status: 'active'
                };
                activeTrades.push(activeTrade);
                console.log(`📋 Загружена активная сделка ${trade.id}, истекает в ${expiresAt.toLocaleTimeString()}`);
              } else {
                console.log(`⏰ Сделка ${trade.id} истекла во время отсутствия, будет закрыта`);
              }
            });
            
            setActiveTrades(activeTrades);
            console.log(`🔥 Загружено ${activeTrades.length} активных сделок`);
          }
          
        } catch (error) {
          console.error('💥 TradingEngine: Ошибка загрузки данных пользователя:', error);
          setBalance(0.00); // Если ошибка, ставим 0
          console.log('💥 TradingEngine: Balance set to $0.00 due to error');
        }
      };
      
      loadUserData();
  }, [user]);

  // Функция обновления баланса на backend
  const updateBackendBalance = async (newBalance) => {
    if (!user) return;
    
    // Проверка на NaN перед отправкой на backend
    const balanceFloat = parseFloat(newBalance);
    if (isNaN(balanceFloat)) {
      console.error('❌ TradingEngine: Cannot update backend with NaN balance:', newBalance);
      return;
    }
    
    // Используем Math.max для предотвращения отрицательного баланса
    const safeBalance = Math.max(0, balanceFloat);
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${getBackendUrl()}/api/trading/balance`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          balance: safeBalance,
          transaction_type: 'trade_result' 
        })
      });
      
      console.log(`🔄 Баланс обновлен на backend: $${safeBalance}`);
    } catch (error) {
      console.error('Ошибка обновления баланса на backend:', error);
    }
  };

  // Функция сохранения завершенной сделки на backend
  const saveCompletedTradeToBackend = async (trade) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getBackendUrl()}/api/trading/save-completed-trade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trade_id: trade.id, // Добавляем уникальный ID для дедупликации на backend
          asset: trade.asset,
          direction: trade.direction,
          amount: trade.amount,
          strike_price: trade.strikePrice,
          close_price: trade.closePrice,
          status: trade.status,
          profit: trade.profit,
          duration: Math.floor((new Date(trade.expiresAt).getTime() - new Date(trade.createdAt).getTime()) / 1000),
          created_at: trade.createdAt.toISOString(),
          expires_at: trade.expiresAt.toISOString()
        })
      });
      
      if (response.ok) {
        console.log(`💾 Завершенная сделка сохранена на backend: ${trade.id}`);
      }
    } catch (error) {
      console.error('Ошибка сохранения завершенной сделки на backend:', error);
    }
  };

  // Функция сохранения открытой сделки на backend
  const saveOpenTradeToBackend = async (trade) => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getBackendUrl()}/api/trading/save-open-trade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trade_id: trade.id, // Добавляем уникальный ID для дедупликации на backend
          asset: trade.asset,
          direction: trade.direction,
          amount: trade.amount,
          strike_price: trade.strikePrice,
          duration: Math.floor((new Date(trade.expiresAt).getTime() - new Date(trade.createdAt).getTime()) / 1000),
          created_at: trade.createdAt.toISOString(),
          expires_at: trade.expiresAt.toISOString()
        })
      });
      
      if (response.ok) {
        const data = await safeJson(response);
        console.log(`💾 Открытая сделка сохранена на backend: ${trade.id}`);
        // Синхронизируем баланс с сервером (единый источник правды)
        if (data.new_balance !== undefined) {
          setBalance(data.new_balance);
          console.log(`💰 Баланс синхронизирован с сервером: $${data.new_balance}`);
        }
      }
    } catch (error) {
      console.error('Ошибка сохранения открытой сделки на backend:', error);
    }
  };

  // Убираем зависимость от цен - сделки теперь зависят только от настроек CRM
  // Цены не нужны для определения результата сделки
  const [assetPrices, setAssetPrices] = useState({});

  // ИСПРАВЛЕНИЕ: Убираем симуляцию цен, используем только реальные данные
  // Цены будут обновляться только из LightweightChart через updateAssetPrice

  // Закрытие сделки — ЗАПРОС РЕЗУЛЬТАТА С БЭКЕНДА (единый источник правды)
  const closeTrade = useCallback(async (tradeId) => {
    console.log(`🔄 Закрытие сделки ${tradeId} — запрос результата с сервера`);
    
    // Находим сделку в активных
    const trade = activeTrades.find(t => t.id === tradeId);
    if (!trade) {
      console.log(`❌ Сделка ${tradeId} не найдена в активных`);
      // Убираем из активных на всякий случай
      setActiveTrades(prev => prev.filter(t => t.id !== tradeId));
      return;
    }

    // Очищаем таймер
    if (tradeTimersRef.current.has(tradeId)) {
      clearTimeout(tradeTimersRef.current.get(tradeId));
      tradeTimersRef.current.delete(tradeId);
    }

    // Для авторизованных — запрос на бэкенд
    if (user) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${getBackendUrl()}/api/trading/close-expired-trade/${tradeId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const result = await safeJson(response);
          console.log(`🎯 Результат с сервера: ${result.status}, profit=${result.profit_loss}`);
          
          const won = result.status === 'won';
          const profit = result.profit_loss || 0;
          
          const closedTrade = {
            ...trade,
            status: result.status,
            closePrice: 0,
            closedAt: new Date(),
            profit: profit
          };
          
          // Обновляем баланс из ответа сервера (точное значение!)
          if (result.new_balance !== undefined) {
            setBalance(result.new_balance);
            console.log(`💰 Баланс обновлён с сервера: $${result.new_balance}`);
          }
          
          // Добавляем в историю
          setTradeHistory(prevHistory => {
            if (prevHistory.some(t => t.id === closedTrade.id)) return prevHistory;
            return [closedTrade, ...prevHistory];
          });
          
          // Убираем из активных
          setActiveTrades(prev => prev.filter(t => t.id !== tradeId));
          return;
        } else {
          console.error(`❌ Ошибка от сервера: ${response.status}`);
        }
      } catch (error) {
        console.error('❌ Ошибка запроса результата:', error);
      }
      
      // Fallback: если сервер недоступен — ждём и пробуем снова через 3 секунды
      console.log(`⏳ Повторная попытка через 3с для ${tradeId}...`);
      setTimeout(() => closeTrade(tradeId), 3000);
      return;
    }
    
    // Для демо-режима (без авторизации) — локальная логика
    const random = Math.random();
    const won = random < 0.7;
    const payoutPercentage = (clientSettings.payoutPercentage || 85) / 100;
    const profit = won ? trade.amount * payoutPercentage : 0;
    
    const closedTrade = {
      ...trade,
      status: won ? 'won' : 'lost',
      closePrice: 0,
      closedAt: new Date(),
      profit: profit
    };
    
    if (won) {
      setBalance(prevBalance => {
        const prev = parseFloat(prevBalance) || 0;
        return prev + parseFloat(trade.amount) + parseFloat(profit);
      });
    }
    
    setTradeHistory(prevHistory => {
      if (prevHistory.some(t => t.id === closedTrade.id)) return prevHistory;
      return [closedTrade, ...prevHistory];
    });
    
    setActiveTrades(prev => prev.filter(t => t.id !== tradeId));
  }, [activeTrades, clientSettings.payoutPercentage, user]);

  // Восстанавливаем таймеры для активных сделок
  useEffect(() => {
    activeTrades.forEach(trade => {
      const now = new Date();
      const expiresAt = new Date(trade.expiresAt);
      const timeLeft = expiresAt.getTime() - now.getTime();
      
      console.log(`🔍 Checking trade ${trade.id}: timeLeft=${Math.floor(timeLeft/1000)}s, expires=${expiresAt.toISOString()}, now=${now.toISOString()}`);
      
      // CRITICAL: Check maxSafeTimeout for long trades (>24.8 days)
      const maxSafeTimeout = 2147483647; // ~24.8 days in milliseconds
      
      // Если сделка еще не истекла и у нее нет таймера
      if (timeLeft > 0 && !tradeTimersRef.current.has(trade.id)) {
        if (timeLeft < maxSafeTimeout) {
          const timerId = setTimeout(() => {
            console.log(`⏰ Восстановленный таймер сработал для сделки ${trade.id}`);
            closeTrade(trade.id);
          }, timeLeft);
          
          tradeTimersRef.current.set(trade.id, timerId);
          console.log(`⏰ Восстановлен таймер для сделки ${trade.id}, осталось ${Math.floor(timeLeft / 1000)}с (${Math.floor(timeLeft/3600000)}ч)`);
        } else {
          console.log(`⏰ Сделка ${trade.id} слишком длинная (${Math.floor(timeLeft/86400000)}д), таймер не устанавливается`);
        }
      } else if (timeLeft <= 0) {
        console.log(`⚠️  Сделка ${trade.id} показывает истекшее время (timeLeft=${timeLeft}ms), НО она в activeTrades - НЕ закрываем автоматически`);
        // НЕ закрываем автоматически - backend уже проверил что она активна
        // Возможно проблема с timezone
      }
    });
  }, [activeTrades, closeTrade]);

  useEffect(() => {
    return () => {
      // Очищаем все таймеры сделок при размонтировании
      tradeTimersRef.current.forEach(timer => clearTimeout(timer));
      tradeTimersRef.current.clear();
      console.log('🧹 Все таймеры очищены при размонтировании');
    };
  }, []);

  // Создание новой сделки
  const createTrade = (asset, direction, amount, expirationTime, strikePriceParam = null) => {
    // Проверка: торговля заблокирована из CRM
    if (!clientSettings.tradingEnabled) {
      return { success: false, message: 'Торговые и финансовые операции для вас временно недоступны. Просим вас обратиться в службу поддержки или связаться с вашим финансовым аналитиком для получения дополнительной информации' };
    }

    // Проверка выходных — биржи закрыты
    const nowCheck = new Date();
    const utcDay = nowCheck.getUTCDay(); // 0=Sun, 5=Fri, 6=Sat
    const utcHour = nowCheck.getUTCHours();
    const marketClosed = (utcDay === 6) || (utcDay === 0 && utcHour < 22) || (utcDay === 5 && utcHour >= 22);
    if (marketClosed) {
      return { success: false, message: 'Биржи закрыты. Торговля доступна с воскресенья 22:00 до пятницы 22:00 (UTC)' };
    }

    if (balance < amount) {
      return { success: false, message: 'Недостаточно средств' };
    }

    // [CFD-2026-05] Lock strike price at trade open
    // Caller (TradingPlatform) passes live bid/ask snapshot — use it.
    // Falls back to assetPrices state if not provided.
    const lockedStrike = strikePriceParam || assetPrices[asset] || 0;
    console.log(`📝 Создание сделки ${asset} ${direction} · strike зафиксирован = ${lockedStrike}`);

    const tradeId = Date.now().toString();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + expirationTime * 1000);

    const newTrade = {
      id: tradeId,
      asset,
      direction, // 'call' или 'put'
      amount,
      strikePrice: lockedStrike, // [CFD-2026-05] FIXED at trade open — never changes
      createdAt: now,
      expiresAt,
      status: 'active',
      payout: 0.85 // 85% выплата
    };

    setBalance(prev => {
      const prevBalance = parseFloat(prev) || 0;
      const tradeAmount = parseFloat(amount) || 0;
      const newBalance = prevBalance - tradeAmount;
      
      // Проверка на NaN
      if (isNaN(newBalance)) {
        console.error('❌ TradingEngine: NaN detected in balance calculation', { prev, amount, prevBalance, tradeAmount });
        return prevBalance; // Возвращаем предыдущий баланс при ошибке
      }
      
      // НЕ вызываем updateBackendBalance — save-open-trade сам обновляет баланс через $inc
      console.log(`💰 Баланс обновлен после сделки: ${prevBalance} -> ${newBalance}`);
      return newBalance;
    });
    setActiveTrades(prev => [...prev, newTrade]);

    // Сохраняем открытую сделку на backend для авторизованных пользователей
    if (user) {
      saveOpenTradeToBackend(newTrade);
    }

    // Установка таймера для закрытия сделки
    // CRITICAL: setTimeout max value is ~24.8 days (2147483647 ms)
    // For trades > 24 days, don't set timer - they will be closed by backend
    const maxSafeTimeout = 2147483647; // ~24.8 days in milliseconds
    const timeoutMs = expirationTime * 1000;
    
    if (timeoutMs < maxSafeTimeout) {
      const timerId = setTimeout(() => {
        console.log(`⏰ Таймер сработал для сделки ${tradeId}`);
        closeTrade(tradeId);
      }, timeoutMs);

      // Сохраняем таймер
      tradeTimersRef.current.set(tradeId, timerId);
      console.log(`⏲️  Установлен таймер для сделки ${tradeId} на ${expirationTime}с`);
    } else {
      console.log(`⏰ Сделка ${tradeId} слишком длинная (${expirationTime}с), таймер не устанавливается. Будет закрыта backend'ом при истечении.`);
    }

    console.log(`🆕 Создана сделка ${tradeId}:`, {
      asset,
      direction,
      amount,
      expirationTime,
      expiresAt: expiresAt.toLocaleTimeString()
    });

    // Для авторизованных пользователей сделки сохраняются при завершении

    return { success: true, trade: newTrade };
  };

  // Duplicate closeTrade function removed - using the one defined earlier

  // Цены больше не используются для определения результата сделки
  // Функция оставлена для обратной совместимости, но возвращает фиксированное значение
  const fetchActualPrice = async (asset) => {
    console.log(`💰 Цена для ${asset} не используется (управление из CRM)`);
    return 100; // Фиксированная цена для отображения
  };

  // ИСПРАВЛЕНИЕ: Функция обновления цены актива из LightweightChart
  const updateAssetPrice = useCallback(async (asset, price) => {
    // Если цена передана из LightweightChart, используем её как единственный источник истины
    if (price && typeof price === 'number' && price > 0) {
      setAssetPrices(prev => {
        const oldPrice = prev[asset];
        const newPrices = { ...prev, [asset]: price };
        
        // Логируем изменение цены только если оно значительное
        if (!oldPrice || Math.abs(oldPrice - price) / oldPrice > 0.001) {
          console.log(`📈 TradingEngine: ${asset} price updated from ${oldPrice?.toFixed(5) || 'N/A'} to ${price.toFixed(5)}`);
        }
        
        return newPrices;
      });
      return;
    }
    
    // Если цена не передана, получаем актуальную цену из API
    const actualPrice = await fetchActualPrice(asset);
    if (actualPrice && actualPrice > 0) {
      setAssetPrices(prev => ({
        ...prev,
        [asset]: actualPrice
      }));
      console.log(`🔄 TradingEngine: Fetched real price for ${asset}: ${actualPrice}`);
    }
  }, []);

  // Функция для принудительной перезагрузки данных
  const refreshData = useCallback(async () => {
    if (user) {
      console.log('🔄 TradingEngine: Manual data refresh triggered');
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // [CFD-2026-05] Re-fetch balance — without this, CFD open/close
          // wouldn't update the balance shown on the platform
          try {
            const balResp = await fetch(`${getBackendUrl()}/api/trading/balance`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (balResp.ok) {
              const balData = await safeJson(balResp);
              const apiBalance = parseFloat(balData.balance);
              if (!isNaN(apiBalance)) {
                setBalance(apiBalance);
                console.log(`💰 Refreshed balance: $${apiBalance}`);
              }
            }
          } catch (e) {
            console.error('❌ Error refreshing balance:', e);
          }

          // Перезагрузка истории сделок (теперь возвращает данные из CRM)
          const tradesResponse = await fetch(`${getBackendUrl()}/api/trading/trades`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (tradesResponse.ok) {
            const tradesData = await safeJson(tradesResponse);
            console.log('🔄 Refreshed trades data from CRM:', tradesData);
            const completedTrades = tradesData.filter(trade => trade.status !== 'active');
            const uniqueCompletedTrades = completedTrades.filter((trade, index, arr) => 
              arr.findIndex(t => t.id === trade.id) === index
            );
            setTradeHistory(uniqueCompletedTrades);
            console.log(`🔄 Refreshed ${uniqueCompletedTrades.length} trades in history`);
          }
          
          // Перезагрузка активных сделок (теперь возвращает данные из CRM)
          const openTradesResponse = await fetch(`${getBackendUrl()}/api/trading/open-trades`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (openTradesResponse.ok) {
            const openTradesData = await safeJson(openTradesResponse);
            console.log('🔄 Raw active trades data from CRM:', openTradesData);
            const now = new Date();
            const activeTrades = [];
            
            openTradesData.forEach(trade => {
              const expiresAt = new Date(trade.expires_at);
              if (expiresAt > now) {
                activeTrades.push({
                  id: trade.id,
                  asset: trade.asset,
                  direction: trade.direction,
                  amount: trade.amount,
                  strikePrice: trade.strike_price,
                  createdAt: new Date(trade.created_at),
                  expiresAt: expiresAt,
                  status: 'active'
                });
                console.log(`🔄 Added active trade: ${trade.asset} expires at ${expiresAt.toLocaleTimeString()}`);
              }
            });
            setActiveTrades(activeTrades);
            console.log(`🔄 Refreshed ${activeTrades.length} active trades`);
          } else {
            console.error('❌ Failed to refresh active trades:', openTradesResponse.status);
          }
          
        } catch (error) {
          console.error('❌ Error refreshing data:', error);
        }
      }
    }
  }, [user]);

  return {
    balance,
    activeTrades,
    tradeHistory,
    assetPrices,
    createTrade,
    closeTrade,
    setBalance,
    refreshData,
    clientSettings,
    setClientSettings,
    updateAssetPrice
  };
};

// Компонент отображения баланса с защитой от NaN
export const BalanceDisplay = ({ balance }) => {
  const safeBalance = (() => {
    if (balance === null || balance === undefined) return 0.00;
    const numBalance = parseFloat(balance);
    return isNaN(numBalance) ? 0.00 : numBalance;
  })();
  
  return (
    <div className="bg-gray-800 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between">
        <span className="text-gray-300 text-sm">{tt('Торговый счет')}</span>
        <span className="text-green-400 font-bold text-lg">${safeBalance.toFixed(2)}</span>
      </div>
    </div>
  );
};

// Optimized ActiveTradeCard with React.memo to prevent unnecessary re-renders
export const ActiveTradeCard = React.memo(({ trade, currentPrice, onTradeExpired }) => {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date().getTime();
      const expiry = new Date(trade.expiresAt).getTime();
      const left = Math.max(0, expiry - now);
      
      setTimeLeft(left);

      // Если время истекло, вызываем callback
      if (left === 0 && onTradeExpired) {
        console.log(`⏰ Время истекло для сделки ${trade.id} в UI`);
        onTradeExpired(trade.id);
      }
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000); // Обновляем каждую секунду, не чаще
    return () => clearInterval(timer);
  }, [trade.expiresAt, trade.id, onTradeExpired]);

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${secs}с`;
    }
  };

  // Открытые сделки всегда показываем в прибыли до закрытия
  const isWinning = true; // Всегда в прибыли до закрытия как требовал пользователь
  
  // Определяем цвет границы в зависимости от оставшегося времени
  const getBorderColor = () => {
    const secondsLeft = Math.floor(timeLeft / 1000);
    if (secondsLeft <= 10) return 'border-red-500';
    if (secondsLeft <= 30) return 'border-yellow-500';
    return 'border-green-500'; // Всегда зеленая для открытых сделок
  };

  return (
    <div className={`bg-gray-800 rounded-lg p-3 mb-2 border-l-4 ${getBorderColor()}`}>
      <div className="flex justify-between items-start mb-2">
        <div>
          <span className="text-white font-semibold">{trade.asset}</span>
          <span className={`ml-2 px-2 py-1 rounded text-xs ${
            trade.direction === 'call' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}>
            {trade.direction === 'call' ? 'ПОКУПКА' : 'ПРОДАЖА'}
          </span>
        </div>
        <span className={`font-bold ${isWinning ? 'text-green-400' : 'text-red-400'}`}>
          {isWinning ? '+' : ''}${(isWinning ? trade.amount * (trade.payout || trade.expected_payout || 1.85) : -trade.amount).toFixed(2)}
        </span>
      </div>
      
      <div className="flex justify-between text-sm text-gray-300 mb-2">
        <span>Сумма: ${trade.amount}</span>
        <span className={`font-bold ${
          timeLeft <= 10000 ? 'text-red-400' : 
          timeLeft <= 30000 ? 'text-yellow-400' : 'text-white'
        } ${Math.floor(Date.now() / 120000) % 2 === 0 && timeLeft <= 10000 ? 'animate-pulse' : ''}`}>
          ⏰ {formatTime(timeLeft)}
        </span>
      </div>
      
      {/* Информация о цене входа удалена */}
    </div>
  );
});

export default useTradingEngine;